import jsPDF from "jspdf";
import html2canvas from "html2canvas";

// Finds a near-white horizontal line (a gap between rows) close to the ideal
// page break so slices don't cut through a table row or a line of text.
function cleanBreak(ctx, w, idealEnd, minEnd) {
  if (idealEnd - minEnd <= 0) return idealEnd;
  let img;
  try {
    img = ctx.getImageData(0, minEnd, w, idealEnd - minEnd).data;
  } catch (_) {
    return idealEnd; // tainted canvas → fall back to a straight cut
  }
  const h = idealEnd - minEnd;
  for (let y = h - 1; y >= 0; y--) {
    let nonWhite = 0;
    const base = y * w * 4;
    for (let x = 0; x < w; x += 6) {
      const i = base + x * 4;
      if (img[i] < 244 || img[i + 1] < 244 || img[i + 2] < 244) {
        nonWhite++;
        if (nonWhite > 1) break;
      }
    }
    if (nonWhite <= 1) return minEnd + y;
  }
  return idealEnd;
}

async function renderCanvas(elementId) {
  const el = document.getElementById(elementId);
  if (!el) return null;
  return html2canvas(el, {
    scale: 2,
    backgroundColor: "#ffffff",
    useCORS: true,
    logging: false,
    windowWidth: el.scrollWidth,
  });
}

// Builds a clean, multi-page A4 PDF from a DOM element, breaking pages on
// whitespace so rows/text are never sliced across two pages.
async function buildPdf(elementId) {
  const canvas = await renderCanvas(elementId);
  if (!canvas) return null;

  const pdf = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const margin = 24;
  const usableW = pageW - margin * 2;
  const usableH = pageH - margin * 2;

  const pxPerPt = canvas.width / usableW;
  const pageSlicePx = Math.floor(usableH * pxPerPt);
  const ctx = canvas.getContext("2d");

  let renderedPx = 0;
  let pageIndex = 0;
  while (renderedPx < canvas.height) {
    let sliceEnd = Math.min(renderedPx + pageSlicePx, canvas.height);
    if (sliceEnd < canvas.height) {
      const minEnd = renderedPx + Math.floor(pageSlicePx * 0.6);
      sliceEnd = cleanBreak(ctx, canvas.width, sliceEnd, minEnd);
    }
    const sliceHeightPx = sliceEnd - renderedPx;

    const pageCanvas = document.createElement("canvas");
    pageCanvas.width = canvas.width;
    pageCanvas.height = sliceHeightPx;
    const pctx = pageCanvas.getContext("2d");
    pctx.fillStyle = "#ffffff";
    pctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
    pctx.drawImage(canvas, 0, renderedPx, canvas.width, sliceHeightPx, 0, 0, canvas.width, sliceHeightPx);

    const imgData = pageCanvas.toDataURL("image/jpeg", 0.95);
    const imgHpt = sliceHeightPx / pxPerPt;
    if (pageIndex > 0) pdf.addPage();
    pdf.addImage(imgData, "JPEG", margin, margin, usableW, imgHpt);

    renderedPx = sliceEnd;
    pageIndex += 1;
  }
  return pdf;
}

export async function downloadElementPdf(elementId, filename) {
  const pdf = await buildPdf(elementId);
  if (pdf) pdf.save(filename);
}

export const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result).split(",")[1] || "");
  r.onerror = reject;
  r.readAsDataURL(file);
});

// Returns the PDF as a File for the Web Share API (direct WhatsApp/email attachment on mobile).
export async function elementPdfFile(elementId, filename) {
  const pdf = await buildPdf(elementId);
  if (!pdf) return null;
  const blob = pdf.output("blob");
  return new File([blob], filename, { type: "application/pdf" });
}
